import { CircularProgress, MenuItem, Select } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import ApiService from "../../../Api/ApiServices";
import { showSnackbar } from "../../Common/Snackbar";

export default function EditMemberProfile() {
  const [relationOptions, setRelationOptions] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [memberData, setMemberData] = useState({});
  const [relation, setRelation] = useState("");
  const [role, setRole] = useState("");

  const [loading, setLoading] = useState(false); // for form submit
  const [isFetching, setIsFetching] = useState(false); // for member fetch
  const [relationsLoading, setRelationsLoading] = useState(false);
  const [rolesLoading, setRolesLoading] = useState(false);

  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation();

  const getMemberInformation = async () => {
    try {
      if (Number(id)) {
        setIsFetching(true);
        const { data } = await ApiService.getMember(id);

        if (!data?.relation_id) {
          showSnackbar({ type: 'error', message: `${t("screens.accountInformations.memberDataNotFound")}` });
          navigate("/myFootprintID");
        }
        setMemberData(data);
        setRelation(data?.relation_id);
        setRole(data?.role_id);
      } else {
        showSnackbar({ type: 'error', message: `${t("screens.accountInformations.memberDataNotFound")}` });
        navigate("/myFootprintID");
      }
    } catch (error) {
      showSnackbar({ type: 'error', message: `${t("screens.accountInformations.errorMessage")}` });

    } finally {
      setIsFetching(false);
    }
  };

  const getRelationships = async () => {
    setRelationsLoading(true);
    try {
      const { data } = await ApiService.relationsEmergency();
      if (data?.length) {
        const mappedData = data.map((item) => ({
          label: item.mem_rel_type,
          value: item.id,
        }));
        const filteredData = mappedData.filter((item) => item.value !== 1);
        setRelationOptions(filteredData);
      }
    } catch (err) {
      console.log("err: ", err);
    } finally {
      setRelationsLoading(false);
    }
  };

  const getRoles = async () => {
    setRolesLoading(true);
    try {
      const { data } = await ApiService.getRoles();
      if (data?.length) {
        const mappedData = data.map((item) => ({
          label: item.name,
          value: item.id,
        }));
        setRoleOptions(mappedData);
      }
    } catch (err) {
      console.log("err: ", err);
    } finally {
      setRolesLoading(false);
    }
  };

  useEffect(() => {
    getRelationships();
    getRoles();
    getMemberInformation();
  }, []);

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...memberData,
        relation_id: relation,
        role_id: role,
      };
      await ApiService.updateMember(id, payload);
      showSnackbar({ type: 'success', message: `${t("screens.accountInformations.memberUpdateSuccessfully")}` });

      navigate("/myFootprintID");
    } catch (error) {
      showSnackbar({ type: 'error', message: `${t("screens.accountInformations.updateFailed")}` });


    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="main">
      <div className="container-forget">
        {isFetching ? (
          <div style={{ textAlign: "center", marginTop: 30 }}>
            <CircularProgress style={{ color: "var(--radium-info-600)" }} />
          </div>
        ) : (
          <form onSubmit={handleUpdateProfile}>
            {role !== 1 && (
              <>
                <h1>{t("screens.accountInformations.memberProfileEdit")}</h1>

                <div className="field-forget">
                  <label>{t("screens.accountInformations.relation")}</label>
                  <div style={{ position: "relative" }}>
                    <Select
                      fullWidth
                      onChange={(e) => setRelation(e.target?.value)}
                      value={relation}
                      disabled={relationsLoading}
                      sx={{
                        height: "40px",
                        "& .MuiOutlinedInput-notchedOutline": {
                          border: "1px solid #3CA899",
                          borderRadius: "20px",
                        },
                        "& .MuiSelect-icon": { color: "#AEAFB4" },
                      }}
                    >
                      {relationsLoading ? (
                        <MenuItem value="">
                          <CircularProgress size={20} />
                          <span style={{ marginLeft: 10 }}>Loading...</span>
                        </MenuItem>
                      ) : relationOptions.length ? (
                        relationOptions.map((item) => (
                          <MenuItem key={item.value} value={item.value}>
                            {item.label}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem value="">
                          {t("screens.formData.noItemFound")}
                        </MenuItem>
                      )}
                    </Select>
                  </div>
                </div>

                <div className="field-forget">
                  <label>{t("screens.accountInformations.permission")}</label>
                  <div style={{ position: "relative" }}>
                    <Select
                      fullWidth
                      onChange={(e) => setRole(e.target?.value)}
                      value={role}
                      disabled={rolesLoading}
                      sx={{
                        height: "40px",
                        "& .MuiOutlinedInput-notchedOutline": {
                          border: "1px solid #3CA899",
                          borderRadius: "20px",
                        },
                        "& .MuiSelect-icon": { color: "#AEAFB4" },
                      }}
                    >
                      {rolesLoading ? (
                        <MenuItem value="">
                          <CircularProgress size={20} />
                          <span style={{ marginLeft: 10 }}>Loading...</span>
                        </MenuItem>
                      ) : roleOptions.length ? (
                        roleOptions.map((item) => (
                          <MenuItem key={item.value} value={item.value}>
                            {item.label}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem value="">
                          {t("screens.formData.noItemFound")}
                        </MenuItem>
                      )}
                    </Select>
                  </div>
                </div>
              </>
            )}

            <button
              type="submit"
              className="btn1-forget"
              disabled={loading || isFetching}
            >
              {loading
                ? t("screens.formData.updating")
                : t("screens.formData.update")}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
